import React, { useCallback, useMemo, useState } from "react";
import Tabs, { TabsProps } from "@amzn/awsui-components-react/polaris/tabs";

import {
  addOrUpdateUrlParameter,
  getValueFromUrlParameter,
  TAB_ID_KEY,
} from "../../../util/urlUtils";
import {
  DEFAULT_VALIDATION_TAB_ID,
  resolveValidationTabId,
  ValidationTabId,
} from "../../appShell/shellRoutes";
import { DashboardTabContent } from "../DashboardTabContent";
import { ProgramTicketsTabContent } from "../programTickets/ProgramTicketsTabContent";
import { StepBreakdownPivot } from "../stepBreakdown/StepBreakdownPivot";
import { PlaceholderPanel } from "./PlaceholderPanel";

export interface ValidationTabsProps {
  /** Test seam; production reads from URL. */
  readonly initialTabId?: ValidationTabId;
}

const TAB_LABELS: Readonly<Record<ValidationTabId, string>> = {
  dashboard: "Dashboard",
  "step-breakdown": "Step Breakdown",
  "program-tickets": "Program Tickets",
  "catalog-issues": "Catalog Issues",
};

const PLACEHOLDER_FEATURE_NAMES: Readonly<
  Record<
    Exclude<
      ValidationTabId,
      "dashboard" | "step-breakdown" | "program-tickets"
    >,
    string
  >
> = {
  "catalog-issues": "Catalog Issues",
};

const TAB_ORDER: readonly ValidationTabId[] = [
  "dashboard",
  "step-breakdown",
  "program-tickets",
  "catalog-issues",
];

function readTabIdFromUrl(): ValidationTabId {
  return resolveValidationTabId(
    getValueFromUrlParameter(TAB_ID_KEY, DEFAULT_VALIDATION_TAB_ID)
  );
}

function renderTabContent(id: ValidationTabId): React.ReactNode {
  if (id === "dashboard") {
    return <DashboardTabContent />;
  }
  if (id === "step-breakdown") {
    return <StepBreakdownPivot />;
  }
  if (id === "program-tickets") {
    return <ProgramTicketsTabContent />;
  }
  return <PlaceholderPanel featureName={PLACEHOLDER_FEATURE_NAMES[id]} />;
}

export const ValidationTabs: React.FC<ValidationTabsProps> = ({
  initialTabId,
}) => {
  const [activeTabId, setActiveTabId] = useState<ValidationTabId>(
    () => initialTabId ?? readTabIdFromUrl()
  );

  const handleChange = useCallback(
    (event: { detail: TabsProps.ChangeDetail }) => {
      const nextTabId = resolveValidationTabId(event.detail.activeTabId);
      addOrUpdateUrlParameter(TAB_ID_KEY, nextTabId);
      setActiveTabId(nextTabId);
    },
    []
  );

  // Use lazy content rendering so each tab's content stays mounted
  // after first activation. Switching tabs then preserves local UI
  // state (expanded rows, toggles, table pagination) and skips
  // recomputing memoized derivations from cached Redux data.
  const tabs = useMemo<TabsProps.Tab[]>(
    () =>
      TAB_ORDER.map((id) => ({
        id,
        label: TAB_LABELS[id],
        contentRenderStrategy: "lazy",
        content: renderTabContent(id),
      })),
    []
  );

  return (
    <Tabs
      data-testid="validation-tabs"
      activeTabId={activeTabId}
      onChange={handleChange}
      tabs={tabs}
    />
  );
};

export default ValidationTabs;
